import '@/assets/main.scss'
import 'primeflex/primeflex.css'
import 'primeflex/themes/primeone-light.css'

import 'primeicons/primeicons.css'

import { createApp } from 'vue'
import PrimeVue from 'primevue/config'

import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import DialogService from 'primevue/dialogservice'

import { DataLoaderPlugin } from 'vue-router/experimental'
import { createPinia } from 'pinia'
import { PiniaColada } from '@pinia/colada'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

import App from '@/App.vue'
import { router } from '@/router'
import { configureSentry } from './sentry'
import { createSentryPiniaPlugin } from '@sentry/vue'

import { i18n } from '@/i18n'
import { AdminPreset } from './themesConfig'
import { showFallbackErrorPage } from '@/composables/useErrorHandler'
import { AppCancelError } from '@/errors'

// Global error handlers - registered before app mounts to catch all errors
// These handlers work even if Vue/Router fails
// Errors still propagate to Sentry and browser console for monitoring
let appMounted = false

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const reason = event.reason

  // Check if this is an AbortError from cancelled requests (e.g., when navigating quickly)
  // These are intentional cancellations, not real errors
  const isAbortError = reason instanceof DOMException && reason.name === 'AbortError'

  // Check if this is an application-level cancellation (e.g., recomputation, scope disposal)
  const isAppCancelError = reason instanceof AppCancelError

  // Check if this is a 503 Service Unavailable error - these are handled by ServiceStatusBanner
  // The error structure from the OpenAPI client can vary, so check multiple possible locations
  const is503 =
    reason?.status === 503 ||
    reason?.response?.status === 503 ||
    reason?.error?.status === 503 ||
    reason?.body?.error === 'external_service_unavailable'

  if (is503 || isAbortError || isAppCancelError) {
    // This is a service unavailability error, handled by ServiceStatusBanner
    // Prevent default to stop error propagation to console
    event.preventDefault()
    return
  }

  console.error('unhandled rejection', reason)
  //showFallbackErrorPage()
})

window.addEventListener('error', (event: ErrorEvent) => {
  const error = event.error

  // Check if this is an AbortError from cancelled requests (e.g., when navigating quickly)
  // These are intentional cancellations, not real errors
  const isAbortError = error instanceof DOMException && error.name === 'AbortError'

  // Check if this is an application-level cancellation (e.g., recomputation, scope disposal)
  const isAppCancelError = error instanceof AppCancelError

  // Check if this is a 503 Service Unavailable error - these are handled by ServiceStatusBanner
  const is503 = error?.error === 'external_service_unavailable'

  if (is503 || isAbortError || isAppCancelError) {
    event.preventDefault()
    return
  }

  console.error('global error', error)
  //showFallbackErrorPage()
})

const app = createApp(App)

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

if (import.meta.env.VITE_SENTRY_DSN) {
  configureSentry(import.meta.env.VITE_SENTRY_DSN, app, router)
  pinia.use(createSentryPiniaPlugin())
}

app.use(PrimeVue, {
  theme: {
    preset: AdminPreset,
    options: {
      darkModeSelector: '.my-app-dark'
    }
  }
})

app.use(pinia)
app.use(PiniaColada, {
  // optional options
})
app.use(i18n)
app.use(DataLoaderPlugin, { router })
app.use(ToastService)
app.use(DialogService)
app.use(ConfirmationService)
app.use(router)
router.onError((err) => {
  console.error('router error:', err)
})

app.mount('#app')

// Mark app as mounted so global handlers know router is available
appMounted = true
